import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import Table, { DateCell, TextCell } from '@/shared/ui/Table';
import DownloadCsv from '@/entities/DownloadCsv';
import StatusIcon from '@/shared/ui/StatusIcon';
import Button from '@/shared/ui/Button';
import Tooltip from '@/shared/ui/Tooltip';
import { IconInfo } from '@/shared/ui/Icons';
import { formatChartDate } from '@/shared/utils/dateFormatters';
import { 
  SORT_BY,
  ORDER,
  STATUSES,
  LIMIT,
  getStatusIconVariant, 
  getStatusMessage,
} from '~/shared/Payouts';
import { useGetRefundsSharedQuery } from '@/shared/api/Payouts';
import { selectPollingInterval } from "@/processes/Settings";
import { useMinerAccount } from "@/processes/MinerAccounts";

type PayoutsOSTableProps = {
  className?: string;
  csvRef?: any;
}

const PayoutsOSTable = (props: PayoutsOSTableProps) => {
  const { 
    className,
    csvRef,
  } = props;
  const intl = useIntl();
  const pollingInterval = useSelector(selectPollingInterval);
  const { miner, coin } = useMinerAccount();

  const { 
    data = {},
    isSuccess,
  } = useGetRefundsSharedQuery({ 
    miner,
    coin,
    limit: LIMIT,
    sortBy: `${ORDER.DESC}${SORT_BY.CREATED_AT}`,
    offset: 0,
  }, { pollingInterval, skip: !(miner && coin) })
  const transactions = _get(data, 'items', []);
  
  return (
    <>
      {/* Inject CSV download in the parent component */}
      {csvRef?.current ? (
        createPortal((
          <DownloadCsv
            prepareData={() => {
              const csvData = [
                [
                  intl.formatMessage({ id: 'payouts.time' }),
                  intl.formatMessage({ id: 'payouts.amount' }),
                  intl.formatMessage({ id: 'payouts.status' }),
                ],
              ];
              transactions.forEach((transaction: any) => {
                const { value, createdAt } = transaction;
                const statusMessage = intl.formatMessage({ id: getStatusMessage(STATUSES.SUCCESS) })
                const time = formatChartDate({ date: createdAt, locale: intl.locale });
                return csvData.push([time, value, statusMessage]);
              });
              return csvData;
            }}
            filename={`${coin} - ${intl.formatMessage({ id: 'payouts.os' })}`}
            title={intl.formatMessage({ id: 'download.csv' })}
          />
        ), csvRef?.current)
      ) : null}

      <Table
        stickyHeader={true}
        data={transactions}
        responsiveColspan={{
          md: 3,
        }}
        columns={[
          {
            Header: intl.formatMessage({ id: 'payouts.time' }),
            accessor: 'createdAt',
            Cell: DateCell,
          }, 
          {
            Header: intl.formatMessage({ id: 'payouts.total' }),
            accessor: 'amount',
            Cell: (props: any) => {
              const { value, row, data } = props;
              const reasonMsg = _get(row, `original.reasonMsg`, null);
              return (
                <div className='inline-flex items-center'>
                  <span className='mr-2'>
                    <TextCell value={value} subValue={coin}/>
                  </span>
                  <Tooltip
                    ToggleComponent={
                      <Button colorSchema='plain' size="xs">
                        <IconInfo/>
                      </Button>
                    }
                  >
                    {reasonMsg}
                  </Tooltip>
                </div>
              )
            }
          }, 
          {
            Header: intl.formatMessage({ id: 'payouts.status' }),
            accessor: 'uuid', 
            Cell: () => {
              return (
                <span className='flex items-center justify-between'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(STATUSES.SUCCESS)}
                    title={intl.formatMessage({ id: getStatusMessage(STATUSES.SUCCESS) })}
                    titleClassName='hidden md:inline-block'
                  />
                </span>
              )
            }
          },
        ]}
      />
    </>
  );
}

export default PayoutsOSTable;
